// 이벤트 CSV 업로드 검증.
// - 범위가 정해진 값이 벗어나면 기본값으로 보정하고, 무엇을 바꿨는지 fixes 로 돌려준다.
// - '무엇이 언제 도는지'를 정하는 값(typeIndex / begin / end)은 절대 보정하지 않고 errors 로 남긴다.
// 서버에도 같은 범위 검사가 있다 (api/services/event.js 의 EVENT_VALUE_RULES). 한쪽만 고치지 말 것.

import {OPERATION_TYPE_TRIGGER, WEEKDAY_ALL, triggerActionList} from '../../shared/utils/triggerEnum.js';

const isNumber = (value) => Number.isFinite(value);
const inRange = (value, min, max) => isNumber(value) && value >= min && value <= max;

// EventEditModal 의 InputNumber min 과 같은 기준 (openLevel / arrived / onTime 은 1 이상)
const ONE_OR_MORE_FIELDS = ['openLevel', 'arrived'];
const ZERO_OR_MORE_FIELDS = ['itemIdx', 'itemMultiple', 'action', 'cooldownMinutes', 'dailyMaxCount'];

// operationType 을 먼저 보정해야 onTime 을 제대로 검사할 수 있으므로 순서가 의미를 갖는다.
const RULES = [
    {field: 'onOff', reason: '0 또는 1', valid: (v) => v === 0 || v === 1, fallback: () => 0},
    {field: 'operationType', reason: '1~5', valid: (v) => [1, 2, 3, 4, 5].includes(v), fallback: () => 1},
    // 발동 가능 요일 비트마스크(일=1 ... 토=64). 비어있으면 매일.
    {field: 'weekdays', reason: '1~127 (요일 비트마스크)', valid: (v) => inRange(v, 1, 127), fallback: () => WEEKDAY_ALL},
    {field: 'startTime', reason: '0~1439 분', valid: (v) => inRange(v, 0, 1439), fallback: () => 0},
    {field: 'maxLevel', reason: '1 이상', valid: (v) => isNumber(v) && v >= 1, fallback: () => 999},
    {field: 'eventIndex', reason: '1 이상', valid: (v) => isNumber(v) && v >= 1, fallback: () => 1},
    {
        field: 'onTime',
        // 주 단위만 요일(0=일)이라 0 을 쓸 수 있고, 나머지는 1 이상이다.
        reason: '주 단위는 0~6 요일, 월 단위는 1~31 일, 그 외는 1 이상',
        valid: (v, entity) => {
            if (entity.operationType === 3) return inRange(v, 0, 6);
            if (entity.operationType === 2) return inRange(v, 1, 31);
            return isNumber(v) && v >= 1;
        },
        fallback: () => 1
    },
    {
        field: 'surpriseTime',
        // 깜짝 이벤트인 경우는 보정하지 않고 아래에서 필수 입력으로 검사한다.
        reason: '깜짝 이벤트가 아니면 0',
        valid: (v, entity, setting) => (setting.isSurpriseEvent ? true : v === 0),
        fallback: () => 0
    },
    ...ONE_OR_MORE_FIELDS.map(field => ({
        field,
        reason: '1 이상의 숫자',
        valid: (v) => isNumber(v) && v >= 1,
        fallback: () => 1
    })),
    ...ZERO_OR_MORE_FIELDS.map(field => ({
        field,
        reason: '0 이상의 숫자',
        valid: (v) => isNumber(v) && v >= 0,
        fallback: () => 0
    })),
];

const isBlank = (csvRow, field) => String(csvRow[field] ?? '').trim() === '';

/**
 * @returns {{errors: string[], fixes: string[]}} entity 는 보정된 값으로 직접 수정된다.
 */
export const checkEventCsvRow = (entity, csvRow, eventTable) => {
    const errors = [];
    const fixes = [];

    const setting = eventTable[entity.typeIndex];
    if (!setting) {
        errors.push(`typeIndex ${entity.typeIndex} 는 Event_Setting.csv 에 없는 이벤트입니다.`);
        return {errors, fixes};
    }

    // Event_Setting.csv 가 관리하는 값 : 비어있으면 채우고, 값이 다르면 사람이 확인해야 한다.
    ['eventType', 'loadTableIdx', 'rewardGroup'].forEach(field => {
        if (isBlank(csvRow, field)) {
            entity[field] = setting[field];
        } else if (String(csvRow[field]).trim() !== String(setting[field])) {
            errors.push(`${field} 가 Event_Setting.csv 값(${setting[field]})과 다릅니다. 입력값: ${csvRow[field]}`);
        }
    });
    // 이벤트별로 조정 가능한 값 : 비어있을 때만 채운다.
    if (isBlank(csvRow, 'openLevel')) entity.openLevel = setting.openLevel;

    RULES.forEach(rule => {
        const before = entity[rule.field];
        if (rule.valid(before, entity, setting)) return;

        const after = rule.fallback(entity, setting);
        entity[rule.field] = after;
        const shown = isBlank(csvRow, rule.field) ? before : String(csvRow[rule.field]).trim();
        fixes.push(`${rule.field}: ${shown} -> ${after} (${rule.reason})`);
    });

    // 깜짝 이벤트는 운영 시간을 기획이 정해야 하므로 임의로 채우지 않는다.
    if (setting.isSurpriseEvent && !(isNumber(entity.surpriseTime) && entity.surpriseTime >= 1)) {
        errors.push('깜짝 이벤트는 surpriseTime(운영 시간, 분)을 1 이상으로 반드시 입력해야 합니다.');
    }

    if (entity.openLevel > entity.maxLevel) {
        errors.push(`openLevel(${entity.openLevel}) 이 maxLevel(${entity.maxLevel}) 보다 큽니다.`);
    }

    // 선행 조건은 기획 의도가 담긴 값이라 보정하지 않고 오류로 남긴다.
    const triggers = Array.isArray(entity.triggers) ? entity.triggers : [];
    if (entity.operationType === OPERATION_TYPE_TRIGGER) {
        if (!setting.isSurpriseEvent) {
            errors.push('선행 조건 발동은 깜짝 이벤트에만 설정할 수 있습니다.');
        }
        if (triggers.length === 0) {
            errors.push('운영 타입이 선행 조건 발동이면 triggers 를 1개 이상 입력해야 합니다.');
        }
        triggers.forEach((trigger, i) => {
            const no = i + 1;
            if (trigger.type !== 1 && trigger.type !== 2) {
                errors.push(`triggers ${no}번째: type 은 1(이벤트 완료) 또는 2(인게임 액션) 여야 합니다.`);
            }
            if (i > 0 && trigger.op !== 0 && trigger.op !== 1) {
                errors.push(`triggers ${no}번째: op 는 0(AND) 또는 1(OR) 이어야 합니다.`);
            }
            if (!(trigger.targetId > 0) || !(trigger.count > 0)) {
                errors.push(`triggers ${no}번째: targetId 와 count 는 1 이상이어야 합니다.`);
            }
            if (trigger.type === 1 && trigger.targetId === entity.typeIndex) {
                errors.push(`triggers ${no}번째: 자기 자신을 선행 조건으로 지정할 수 없습니다.`);
            }
            if (trigger.type === 2 && !triggerActionList.some(action => action.value === trigger.targetId)) {
                errors.push(`triggers ${no}번째: 알 수 없는 인게임 액션입니다. (targetId ${trigger.targetId})`);
            }
        });
    } else if (triggers.length > 0) {
        errors.push('운영 타입이 선행 조건 발동이 아닌데 triggers 가 설정되어 있습니다.');
    }

    return {errors, fixes};
};
